import type { Candidate } from "../api/searches";

// Names as the contact will carry them. A confirmed record names the contact
// — its roster form, made readable — because that is the name the mailroom
// matches against; what the user typed was the search key. Without a record,
// the typed name stands, tidied only when it was typed in one case.

export const titleCase = (s: string): string =>
  s.toLowerCase().replace(/(^|[\s'’\p{Pd}])(\p{L})/gu, (m, sep, ch) => sep + ch.toUpperCase());

// Tidied only when written in one case ("MARCUS", "de la cruz"); a name that
// already carries its own casing ("McDonald", "de la Cruz") is left alone —
// a title-caser would only mangle it.
const tidy = (s: string): string => {
  const t = s.trim();
  if (!t) return t;
  return t === t.toUpperCase() || t === t.toLowerCase() ? titleCase(t) : t;
};

// The name to say: the first token of a roster first name, which may carry a
// middle name ("Marcus Dewayne" is stored, "Marcus" is addressed).
export const callName = (first: string): string => first.trim().split(/\s+/)[0] || "";

// Generational suffixes keep their own form and always trail the name.
const SUFFIX = /^(JR|SR|II|III|IV)\.?$/i;
const formatSuffix = (s: string): string => {
  const u = s.toUpperCase().replace(/\.$/, "");
  return u === "JR" || u === "SR" ? `${u.charAt(0)}${u.slice(1).toLowerCase()}.` : u;
};
const splitSuffix = (s: string): [string, string | null] => {
  const parts = s.trim().split(/\s+/);
  if (parts.length > 1 && SUFFIX.test(parts[parts.length - 1])) {
    return [parts.slice(0, -1).join(" "), formatSuffix(parts[parts.length - 1])];
  }
  return [s.trim(), null];
};

export type RecordName = { first: string; last: string; suffix: string | null };

// Records list people as "LAST, FIRST". Anything with more than one comma
// (middle names in odd places) can't be split with confidence; the source's
// own first/last fields are used then, and failing those, nothing.
export const recordName = (c: Candidate): RecordName | null => {
  const raw = (c.name || "").trim();
  const segments = raw
    .split(",")
    .map((s) => s.trim())
    .filter(Boolean);
  if (segments.length === 2) {
    const [last, rest] = segments;
    // "MARCUS REED, JR." is a suffix, not a last name.
    if (SUFFIX.test(rest)) {
      const [name, suffix] = splitSuffix(`${last} ${rest}`);
      const words = name.split(/\s+/);
      if (words.length < 2) return null;
      return { first: tidy(words.slice(0, -1).join(" ")), last: tidy(words[words.length - 1]), suffix };
    }
    const [first, suffix] = splitSuffix(rest);
    const [lastName, lastSuffix] = splitSuffix(last);
    return { first: tidy(first), last: tidy(lastName), suffix: suffix || lastSuffix };
  }
  if (c.firstName && c.lastName) {
    const [first, suffix] = splitSuffix(c.firstName);
    const [last, lastSuffix] = splitSuffix(c.lastName);
    return { first: tidy(first), last: tidy(last), suffix: suffix || lastSuffix };
  }
  if (segments.length === 1) {
    const [name, suffix] = splitSuffix(raw);
    const words = name.split(/\s+/);
    if (words.length < 2) return null;
    return { first: tidy(words.slice(0, -1).join(" ")), last: tidy(words[words.length - 1]), suffix };
  }
  return null;
};

// The person as the card shows them: "Marcus Reed Jr." — or the record's own
// form, tidied, when it can't be split.
export const displayName = (c: Candidate): string => {
  const parts = recordName(c);
  if (!parts) return tidy(c.name || "");
  return [parts.first, parts.last, parts.suffix].filter(Boolean).join(" ");
};

// What the user typed, tidied by the same rule.
export const typedName = (s: string): string => tidy(s);

// What the contact is called: the confirmed record's name when there is one
// (the suffix rides with the last name), the typed name otherwise.
export const contactName = (
  typed: { firstName: string; lastName: string },
  candidate?: Candidate | null,
): { first: string; last: string } => {
  const record = candidate ? recordName(candidate) : null;
  if (record) return { first: record.first, last: [record.last, record.suffix].filter(Boolean).join(" ") };
  return { first: typedName(typed.firstName), last: typedName(typed.lastName) };
};
